import type { ReactNode } from 'react';
import type { TypeDeVin } from '../domain/cellar';
import { COUNTRIES } from '../domain/countries';
import { isRegionOf, REGION_OTHER, regionsFor } from '../domain/regions';
import { countryLabel, sortedByLabel, t } from '../i18n';
import { GrapePicker } from './GrapePicker';
import { vintageChoices } from '../domain/form-choices';
import { grapesForPlace } from '../domain/region-grapes';
import { currentMonth } from '../domain/months';
import { wineTypeLabel } from '../i18n/errors';

/** Controlled form values for a wine's identity fields (FR-001, FR-004, FR-005). */
export interface WineIdentityFormValue {
  domaine: string;
  cepage: string;
  /** '' means no Parcelle (an empty Parcelle is itself a value, per FR-003). */
  parcelle: string;
  /** Raw text; the caller parses and validates it as a whole number. */
  annee: string;
  /** An ISO country code from COUNTRIES, or '' if not yet chosen. */
  pays: string;
  /** One of the country's regions or "other" (required, owner 2026-10-03), or '' if not chosen. */
  region: string;
  typeDeVin: TypeDeVin | '';
}

const WINE_TYPES: TypeDeVin[] = ['rouge', 'blanc', 'rose', 'vendanges_tardives'];

export function emptyWineIdentity(): WineIdentityFormValue {
  return {
    domaine: '',
    cepage: '',
    parcelle: '',
    annee: '',
    pays: '',
    region: '',
    typeDeVin: '',
  };
}

interface Props {
  value: WineIdentityFormValue;
  onChange: (value: WineIdentityFormValue) => void;
  /** The cellar's grape varieties to choose from (domain/grapes.ts `grapeChoices`). */
  grapes: readonly string[];
  /** More fields at the end of "Détails du vin" (the drink-by year, owner 2026-10-06). */
  children?: ReactNode;
  /** Hides the "Détails du vin" legend from sight, for a section already titled so. */
  hideLegend?: boolean;
}

/**
 * Domaine, Cépage, Parcelle, Année, Pays, Région and Type de vin (FR-001, FR-005). The region
 * (required, owner 2026-10-03) is chosen from the country's list or "Autre région", and stays
 * disabled until a country is chosen; changing the country clears a region that is not its own.
 * The Cépage is ticked in a searchable list of varieties (owner, 2026-10-04). Owner, 2026-10-08:
 * the order is Type, Domaine, Parcelle, Année (picked in a list), Pays, Région, Cépage, then the
 * caller's own fields (the drink-by year).
 */
export function WineIdentityFields({ value, onChange, grapes, children, hideLegend }: Props) {
  const regions = regionsFor(value.pays);

  function set<K extends keyof WineIdentityFormValue>(
    key: K,
    fieldValue: WineIdentityFormValue[K],
  ) {
    onChange({ ...value, [key]: fieldValue });
  }

  return (
    <fieldset>
      <legend {...(hideLegend ? { className: 'visually-hidden' } : {})}>{t('wine.legend')}</legend>

      <label htmlFor="typeDeVin">{t('wine.typeDeVin')}</label>
      <select
        id="typeDeVin"
        required
        value={value.typeDeVin}
        onChange={(e) => set('typeDeVin', e.target.value as TypeDeVin)}
      >
        <option value="" disabled>
          {t('wine.chooseType')}
        </option>
        {sortedByLabel(WINE_TYPES, wineTypeLabel).map((code) => (
          <option key={code} value={code}>
            {wineTypeLabel(code)}
          </option>
        ))}
      </select>
      <label htmlFor="domaine">{t('wine.domaine')}</label>
      <input
        id="domaine"
        required
        value={value.domaine}
        onChange={(e) => set('domaine', e.target.value)}
      />

      <label htmlFor="parcelle">{t('wine.parcelle')}</label>
      <input
        id="parcelle"
        value={value.parcelle}
        onChange={(e) => set('parcelle', e.target.value)}
      />

      <label htmlFor="annee">{t('wine.annee')}</label>
      <select
        id="annee"
        required
        value={value.annee}
        onChange={(e) => set('annee', e.target.value)}
      >
        <option value="" disabled>
          {t('wine.chooseAnnee')}
        </option>
        {vintageChoices(currentMonth(), value.annee === '' ? undefined : Number(value.annee)).map(
          (year) => (
            <option key={year} value={String(year)}>
              {year}
            </option>
          ),
        )}
      </select>

      <label htmlFor="pays">{t('wine.pays')}</label>
      <select
        id="pays"
        required
        value={value.pays}
        onChange={(e) =>
          onChange({
            ...value,
            pays: e.target.value,
            region: isRegionOf(e.target.value, value.region) ? value.region : '',
          })
        }
      >
        <option value="" disabled>
          {t('wine.choosePays')}
        </option>
        {sortedByLabel(COUNTRIES, (country) => countryLabel(country.code)).map((country) => (
          <option key={country.code} value={country.code}>
            {countryLabel(country.code)}
          </option>
        ))}
      </select>

      <label htmlFor="region">{t('wine.region')}</label>
      <select
        id="region"
        required
        value={value.region}
        disabled={regions.length === 0}
        onChange={(e) => set('region', e.target.value)}
      >
        <option value="" disabled>
          {value.pays === '' ? t('wine.regionNeedsCountry') : t('wine.chooseRegion')}
        </option>
        {regions.map((region) => (
          <option key={region} value={region}>
            {region}
          </option>
        ))}
        {regions.length > 0 && <option value={REGION_OTHER}>{t('wine.regionOther')}</option>}
      </select>

      <label htmlFor="cepage">{t('wine.cepage')}</label>
      <GrapePicker
        buttonId="cepage"
        value={value.cepage}
        choices={grapes}
        listed={grapesForPlace(value.pays, value.region)}
        onChange={(cepage) => set('cepage', cepage)}
      />

      {children}
    </fieldset>
  );
}
